import { Box, Icon, Text } from '@medely/ui-kit';
import React from 'react';
import { JobDetailsListItem } from '../../JobDetailsListItem';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { COVID_REQUIREMENTS } from '@mp/constants';
import { DisplayHelpers } from '@mp/utils';
import { BookedGeneralInfo } from './BookedGeneralInfo';
import { AvailableGeneralInfo } from './AvailableGeneralInfo';
import { useCurrentUser } from '@mp/hooks';
import { JobGeneralInfoProps } from './interface';

export const JobGeneralInfo = ({
  job,
  smallIcon,
  bookedOrInProgressOrCompleted,
}: JobGeneralInfoProps) => {
  const { isSaas, isAgency, currentUser } = useCurrentUser();
  const covidRequirement =
    job.location?.covid_requirement || job.location?.state?.covid_requirement;
  const covidText =
    COVID_REQUIREMENTS.info[covidRequirement as keyof typeof COVID_REQUIREMENTS.info] ?? '';

  const isHighRiskPosition =
    job.positions.some((p) => p.high_risk) && !bookedOrInProgressOrCompleted;

  return (
    <>
      {bookedOrInProgressOrCompleted ? (
        <BookedGeneralInfo
          job={job}
          isSaas={isSaas}
          coordinates={currentUser?.coordinates}
          bookedOrInProgressOrCompleted={bookedOrInProgressOrCompleted}
          smallIcon
        />
      ) : (
        <AvailableGeneralInfo
          job={job}
          isSaas={isSaas}
          coordinates={currentUser?.coordinates}
          isAgency={isAgency}
          bookedOrInProgressOrCompleted={bookedOrInProgressOrCompleted}
          smallIcon
        />
      )}
      {job.specialties && (
        <Box py={1.5}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-star-of-life"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Specialties"
          >
            <Text size="m">{DisplayHelpers.job.specialties(job)}</Text>
            {isHighRiskPosition && (
              <Box
                bgColor="state.info.backing-1"
                p={0.5}
                width={smallIcon ? '30%' : '18%'}
                borderRadius="5px"
                mt={1}
              >
                <Text
                  size="s"
                  color="state.info.secondary"
                  textAlign="center"
                  testId="specialty-required-badge"
                >
                  Required
                </Text>
              </Box>
            )}
          </JobDetailsListItem>
        </Box>
      )}
      {job.surgery_types && (
        <Box py={1.5}>
          <JobDetailsListItem
            icon={
              <Icon
                name="ligh-scalpel"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Procedures"
          >
            <Text size="m">{DisplayHelpers.job.procedures(job)}</Text>
          </JobDetailsListItem>
        </Box>
      )}
      <Box py={1.5}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-head-side-medical"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          title="Patient population"
        >
          <Text size="m">{DisplayHelpers.job.patientPopulation(job)}</Text>
        </JobDetailsListItem>
      </Box>
      {covidRequirement && (
        <Box mb={3} testId="job-highlights-covid-requirement">
          <JobDetailsListItem
            icon={
              <Icon
                name="light-syringe"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Covid protocol"
          >
            <>
              <Text size="m">{covidText}</Text>
              <Box testId="job-highlights-covid-requirement-link">
                <HelpCenterLink text="Learn more" configKey="covidVaccineOrTest" />
              </Box>
            </>
          </JobDetailsListItem>
        </Box>
      )}
    </>
  );
};
